import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { useNotification } from "../../NotificationContext";
import { LabOrder, LabTest } from "../../interfaces/LabOrder";
import { getPatients } from "../../apis/patient";
import { format } from "date-fns";
import { getLabTestsByPatient } from "../../apis/LabOrder";
import { processPayment } from "../../apis/payment";
import {
  createLabOrder,
  updateLabOrder,
  getLabOrderById,
} from "../../apis/LabOrder";

const { Option } = Select;
interface AddLabOrderProps {
  orderId: number | null;
  visitId?: number;
  patientId?: number; 
  closeModal: () => void;
  onOrderCreated?: (order: any) => void;
  hideBillingFields?: boolean; 
  isFromIPD?: boolean;
}

const AddLabOrder: React.FC<AddLabOrderProps> = ({
  orderId,
  visitId,
  patientId,
  closeModal,
  onOrderCreated,
  hideBillingFields = false,
  isFromIPD = false, 
}) => {
  const { showNotification } = useNotification();
  const [patients, setPatients] = useState<Array<any>>([]);
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [availableLabTests, setAvailableLabTests] = useState<LabTest[]>([]);
  const [labOrder, setLabOrder] = useState<LabOrder>({
    orderNumber: `LAB-${new Date().getFullYear()}-${String(
      Math.floor(Math.random() * 1000)
    ).padStart(3, "0")}`,
    patientId: 0,
    visitId: undefined,
    status: "ordered",
    orderDate: new Date().toISOString(),
    priority: "routine",
    items: [],
    notes: "",
    referredBy: "",
  });
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Discount type: "amount" or "percentage"
  type DiscountType = "amount" | "percentage";
  const [discountType, setDiscountType] = useState<DiscountType>("amount");
  const [discountInput, setDiscountInput] = useState<number>(0);

  // Calculate subtotal from selected tests' default_charge
  const subtotal = labOrder.items.reduce(
    (sum, test) => sum + (Number(test.defaultCharge) || 0),
    0
  );

  // Calculate discount amount based on type (percentage or amount)
  // This is the actual discount amount that will be saved to database
  const calculatedDiscountAmount =
    discountType === "percentage"
      ? Math.min((subtotal * discountInput) / 100, subtotal)
      : Math.min(discountInput, subtotal);

  // Total amount after discount
  const totalAmount = Math.max(subtotal - calculatedDiscountAmount, 0);

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
  if (patientId) {
    setLabOrder((prev) => ({
      ...prev,
      patientId: patientId,
      visitId: visitId,
    }));
  }
}, [patientId, visitId]);

  useEffect(() => {
    if (orderId) {
      setIsFetching(true);
      getLabOrderById(orderId)
        .then((res) => {
          console.log("BACKEND ORDER RESPONSE:", res.data.data);
          const order = res.data.data;

          // Load discount amount if it exists
          // When loading, we'll set it as amount type by default
          if (order.discount !== undefined && order.discount !== null) {
            const savedDiscount = Number(order.discount) || 0;
            setDiscountType("amount");
            setDiscountInput(savedDiscount);
          }

          setLabOrder({
            orderNumber: order.orderNumber || labOrder.orderNumber,
            patientId: order.patientId,
            visitId: order.visitId,
            status: order.status || "pending",
            orderDate: order.orderDate || new Date().toISOString(),
            priority: order.priority || "routine",
            notes: order.notes || "",

            items: (order.items || []).map((it: any) => ({
              id: it.labTest?.id ?? it.id, // actual test id
              name: it.labTest?.name ?? it.name, // actual name
              code: it.labTest?.code ?? it.code,
              normalRange: it.labTest?.normalRange,
              unit: it.labTest?.unit,
              defaultCharge: Number(
                it.labTest?.default_charge ??
                  it.labTest?.defaultCharge ??
                  it.defaultCharge ??
                  0
              ),
              status: it.status || "pending",
            })),
          });
        })
        .catch((err) => console.log("Error fetching order:", err))
        .finally(() => setIsFetching(false));
    }
  }, [orderId]);

  const fetchPatients = () => {
    getPatients({ per_page: 100 })
      .then((res: any) => {
        setPatients(res.data.data || []);
      })
      .catch((err: any) => {
        console.log("Error fetching patients:", err);
      });
  };

  const fetchLabTestsByPatient = (
    selectedPatientId?: number,
    search?: string
  ) => {
    if (!selectedPatientId) {
      setAvailableLabTests([]);
      return;
    }

    const params: any = {
      per_page: 15,
    };

    if (search && search.trim() !== "") {
      params.search = search.trim();
    }

    getLabTestsByPatient(selectedPatientId, params)
      .then((res: any) => {
        // API is paginated; list is in res.data.data (array)
        const tests = (res.data?.data || []).map((test: any) => ({
          ...test,
          defaultCharge: Number(test.default_charge || test.defaultCharge || 0),
        }));
        setAvailableLabTests(tests);
      })
      .catch((err: any) => {
        console.log("Error fetching tests by patient:", err);
        setAvailableLabTests([]);
      });
  };

  useEffect(() => {
    fetchLabTestsByPatient(labOrder.patientId);
  }, [labOrder.patientId]);

  const validateForm = () => {
    const validationErrors: any = {};

    if (!labOrder.patientId || labOrder.patientId === 0) {
      validationErrors.patientId = "Patient is required";
    }

    if (!labOrder.items || labOrder.items.length === 0) {
      validationErrors.tests = "At least one test is required";
    }

    const hasErrors = Boolean(Object.keys(validationErrors).length);
    return { hasErrors, validationErrors };
  };

  const handleAddTest = () => {
    if (!selectedTestId) {
      showNotification("error", "Please select a test", "", "topRight");
      return;
    }

    const test = availableLabTests.find((t) => t.id === selectedTestId);
    if (!test) return;

    // Check if test already exists
    if (labOrder.items.some((t) => t.code === test.code)) {
      showNotification("error", "This test is already added", "", "topRight");
      return;
    }

    const newTest: LabTest = {
      // id: Date.now(), // Temporary ID
      id: test.id,
      name: test.name,
      code: test.code,
      normalRange: test.normalRange,
      unit: test.unit,
      status: "pending",
    };

    setLabOrder({
      ...labOrder,
      items: [...labOrder.items, newTest],
    });

    setSelectedTestId(null);
  };

  const handleRemoveTest = (testId: number | undefined) => {
    if (testId) {
      setLabOrder({
        ...labOrder,
        items: labOrder.items.filter((t) => t.id !== testId),
      });
    }
  };

  // const handleSave = () => {
  //   const { hasErrors, validationErrors } = validateForm();

  //   if (hasErrors) {
  //     setErrors(validationErrors);
  //     showNotification("error", "Please fill all required fields", "", "topRight");
  //     return;
  //   }

  //   setIsLoading(true);

  //   const payload = {
  //     patient_id: labOrder.patientId,
  //     visit_id: labOrder.visitId,
  //     orderDate: labOrder.orderDate,
  //     priority: labOrder.priority,
  //     notes: labOrder.notes,
  //     instructions: labOrder.instructions,

  //     items: labOrder.items.map((t) => ({
  //       lab_test_id: t.id,
  //       status: t.status
  //     })),
  //   };

  //   const apiCall = orderId
  //     ? updateLabOrder(orderId, payload)
  //     : createLabOrder(payload);

  //   apiCall
  //   .then((res: any) => {
  //     showNotification(
  //       "success",
  //       orderId ? "Lab order updated successfully" : "Lab order created successfully",
  //       "",
  //       "topRight"
  //     );

  //     if (!orderId && onOrderCreated) {
  //       onOrderCreated(res.data);
  //     }

  //     closeModal();
  //   })
  //   .catch((err: any) => {
  //     console.log("Error saving order:", err);
  //     showNotification("error", "Something went wrong", "", "topRight");
  //   })
  //   .finally(() => setIsLoading(false));

  // };

  const handleSave = async () => {
    const { hasErrors, validationErrors } = validateForm();

    if (hasErrors) {
      setErrors(validationErrors);
      showNotification(
        "error",
        "Please fill all required fields",
        "",
        "topRight"
      );
      return;
    }

    setIsLoading(true);

    try {
      // Calculate discount per test proportionally
      const itemsWithDiscount = labOrder.items.map((test) => {
        const testCharge = Number(test.defaultCharge) || 0;

        // Calculate proportional discount for this test
        // Formula: (test charge / subtotal) * total discount
        const testDiscountAmount =
          subtotal > 0 ? (testCharge / subtotal) * calculatedDiscountAmount : 0;

        // Calculate amount after discount for this test
        const testAmountAfterDiscount = testCharge - testDiscountAmount;

        return {
          lab_test_id: test.id,
          status: test.status,
          charge: testCharge, // Original charge
          discount: Number(testDiscountAmount.toFixed(2)), // Proportional discount for this test
          amount: Number(testAmountAfterDiscount.toFixed(2)), // Final amount after discount
        };
      });

      // Build the payload with itemsWithDiscount
      const payload = {
        patient_id: labOrder.patientId,
        visit_id: labOrder.visitId,
        referred_by: labOrder.referredBy,
        source_type: isFromIPD ? "pathology" : "lab",
        orderDate: labOrder.orderDate,
        priority: labOrder.priority,
        notes: labOrder.notes,
        discount: Number(calculatedDiscountAmount.toFixed(2)), // Total discount
        total_amount: Number(totalAmount.toFixed(2)), // Total amount after discount
        items: itemsWithDiscount, // ✅ Use itemsWithDiscount here, not labOrder.items
      };

      if (orderId) {
        // Edit mode
        await updateLabOrder(orderId, payload);
        showNotification(
          "success",
          "Lab order updated successfully",
          "",
          "topRight"
        );
      } else {
        // Create mode
        // const res = await createLabOrder(payload);
        // if (onOrderCreated) onOrderCreated(res.data);
        const res: any = await createLabOrder(payload);
        const createdOrder = res.data.data;
        onOrderCreated?.(createdOrder);

        showNotification(
          "success",
          "Lab order created successfully",
          "",
          "topRight"
        );
        if (onOrderCreated) onOrderCreated(createdOrder);
      }

      closeModal();
    } catch (err) {
      console.log("Error saving order:", err);
      showNotification("error", "Something went wrong", "", "topRight");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Patient Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Patient <span className="text-red-500">*</span>
          </label>

          {patientId ? (
            // IPD CASE – READ ONLY
            <input
              type="text"
              value={
                patients.find((p) => p.id === labOrder.patientId)
                  ? `${patients.find((p) => p.id === labOrder.patientId).firstName} ${patients.find((p) => p.id === labOrder.patientId).lastName}`
                  : "Loading..."
              }
              disabled
              className="block w-full mt-1 border-gray-300 rounded-md bg-gray-100 text-gray-700 sm:text-sm"
            />
          ) : (
            // OPD CASE – DROPDOWN
            <Select
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
              placeholder="Select patient"
              value={labOrder.patientId || undefined}
              onChange={(value) =>
                setLabOrder({ ...labOrder, patientId: value, items: [] })
              }
              className="mt-1"
            >
              {patients.map((patient) => (
                <Option key={patient.id} value={patient.id}>
                  {patient.firstName} {patient.lastName} - {patient.phone}
                </Option>
              ))}
            </Select>
          )}

          {errors.patientId && (
            <span className="text-sm text-red-500">{errors.patientId}</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Priority
            </label>
            <Select
              style={{ width: "100%" }}
              value={labOrder.priority}
              onChange={(value) =>
                setLabOrder({ ...labOrder, priority: value })
              }
              className="mt-1"
            >
              <Option value="routine">Routine</Option>
              <Option value="stat">Stat</Option>
            </Select>
          </div>

          {/* Order Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Order Date
            </label>
            <input
              type="datetime-local"
              value={format(new Date(labOrder.orderDate), "yyyy-MM-dd'T'HH:mm")}
              onChange={(e) =>
                setLabOrder({
                  ...labOrder,
                  orderDate: new Date(e.target.value).toISOString(),
                })
              }
              className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            />
          </div>
        </div>

        {/* Referred By */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Referred By
          </label>
          <input
            type="text"
            value={labOrder.referredBy || ""}
            onChange={(e) =>
              setLabOrder({ ...labOrder, referredBy: e.target.value })
            }
            className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            placeholder="Enter doctor or source name"
          />
        </div>

        {/* Add Tests */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Tests <span className="text-red-500">*</span>
          </label>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            showSearch
            filterOption={false}
            placeholder="Select test to add"
            onSearch={(value) =>
              fetchLabTestsByPatient(labOrder.patientId, value)
            }
            value={labOrder.items
              .map((t) => t.id)
              .filter((id): id is number => id !== undefined)}
            onChange={(selectedIds: number[]) => {
              const selectedTests = availableLabTests
                .filter((t) => t.id !== undefined && selectedIds.includes(t.id))
                .map((t) => ({
                  id: t.id,
                  name: t.name,
                  code: t.code,
                  normalRange: t.normalRange,
                  unit: t.unit,
                  defaultCharge: Number(t.defaultCharge) || 0,
                  status: "pending" as const,
                }));

              setLabOrder({ ...labOrder, items: selectedTests });
            }}
          >
            {availableLabTests.map((test) => (
              <Option key={test.name} value={test.id}>
                {test.name}
              </Option>
            ))}
          </Select>

          {errors.tests && (
            <span className="text-sm text-red-500">{errors.tests}</span>
          )}
        </div>

        {/* Selected Tests List */}
        {labOrder.items.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-lg">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">
                Selected Tests ({labOrder.items.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {labOrder.items.map((test) => (
                <div
                  key={test.id}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {test.name}
                    </div>
                    {test.code && (
                      <div className="text-xs text-gray-500">
                        {test.code}
                        {test.normalRange &&
                          ` | Normal Range: ${test.normalRange}`}
                      </div>
                    )}

                    <div className="text-xs text-gray-700 mt-1">
                      ₹ {test.defaultCharge ?? 0}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveTest(test.id)}
                    className="ml-4 text-sm text-red-600 hover:text-red-900"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {!hideBillingFields && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/*  Total Amount (Auto-calculated) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Total Amount
            </label>
            <input
              type="text"
              value={`₹ ${totalAmount.toFixed(2)}`}
              disabled
              className="block w-full mt-1 border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-700 sm:text-sm"
            />
          </div>

          {/* Discount */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Discount
            </label>
            <div className="flex gap-2 mt-1">
              <Select
                value={discountType}
                onChange={(value) => {
                  setDiscountType(value);
                  // Reset input when switching types
                  setDiscountInput(0);
                }}
                style={{ width: 70 }}
              >
                <Option value="amount">₹ </Option>
                <Option value="percentage">% </Option>
              </Select>
              <input
                type="number"
                min={0}
                max={discountType === "percentage" ? 100 : subtotal}
                step="0.01"
                value={discountInput}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  if (discountType === "percentage") {
                    setDiscountInput(Math.min(value, 100));
                  } else {
                    setDiscountInput(Math.min(value, subtotal));
                  }
                }}
                className="flex-1 border-gray-300 p-1 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                placeholder={
                  discountType === "percentage"
                    ? "Enter discount %"
                    : "Enter discount amount"
                }
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {discountType === "percentage" ? (
                <>
                  Disc: {discountInput}% = ₹{" "}
                  {calculatedDiscountAmount.toFixed(2)} (on ₹{" "}
                  {subtotal.toFixed(2)})
                </>
              ) : (
                <>
                  Disc Amt: ₹ {calculatedDiscountAmount.toFixed(2)} (on ₹{" "}
                  {subtotal.toFixed(2)})
                </>
              )}
            </p>
          </div>
        </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Notes
          </label>
          <textarea
            rows={3}
            value={labOrder.notes}
            onChange={(e) =>
              setLabOrder({ ...labOrder, notes: e.target.value })
            }
            className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            placeholder="Additional notes..."
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-4 pt-4 mt-6 border-t border-gray-200">
        <button
          onClick={closeModal}
          type="button"
          className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {isLoading ? "Saving..." : orderId ? "Update Order" : "Create Order"}
        </button>
      </div>
    </>
  );
};

export default AddLabOrder;
